import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useStickyPosition } from '../../../hooks/useStickyPosition';
import { isEditorFullscreen } from '../../../util/EditorFullscreen';

export type ToolbarWrapperProps = PropsWithChildren<{
  id?: string;
  className?: string;
}>;

/**
 * A wrapper component that ensures the toolbar is always visible.
 * The toolbar will be fixed to the top of the screen when the user scrolls past it.
 * Its horizontal box is synced to the placeholder holding its place in the flow.
 *
 */
export const ToolbarWrapper: React.FC<ToolbarWrapperProps> = ({
  children,
  id = 'toolbarWrapper',
  className,
}) => {
  const toolbarRef = useRef<HTMLDivElement>(null);
  const placeholderRef = useRef<HTMLDivElement>(null);
  const naturalWidthRef = useRef<number | null>(null);

  const isPinned = useCallback(
    () => toolbarRef.current?.style.position === 'fixed',
    [],
  );

  // While fixed, `width: 100%` would resolve against the viewport, so the
  // horizontal box has to be written explicitly.
  const syncHorizontalBox = useCallback(() => {
    const toolbar = toolbarRef.current;
    const placeholder = placeholderRef.current;
    if (!toolbar || !placeholder || !isPinned()) {
      return;
    }
    const { left, width } = placeholder.getBoundingClientRect();
    if (width === 0) {
      return;
    }
    toolbar.style.left = `${left}px`;
    toolbar.style.width = `${width}px`;
  }, [isPinned]);

  // The column sits in a flex row beside the properties panel, so the reserved
  // width feeds its min-content and would floor this reading. Release it for
  // the measurement -- never widen it, which would squeeze the panel out.
  const measureAvailableWidth = useCallback((): number | null => {
    const toolbar = toolbarRef.current;
    const placeholder = placeholderRef.current;
    const parent = toolbar?.parentElement;
    if (!toolbar || !placeholder || !parent || isEditorFullscreen()) {
      return null;
    }

    const reservedWidth = placeholder.style.width;
    placeholder.style.width = '';

    const { paddingLeft, paddingRight } = getComputedStyle(parent);
    const measured =
      parent.clientWidth -
      Number.parseFloat(paddingLeft) -
      Number.parseFloat(paddingRight);

    placeholder.style.width = reservedWidth;

    return measured > 0 ? measured : null;
  }, []);

  const refreshReservedWidth = useCallback((): void => {
    const placeholder = placeholderRef.current;
    if (!isPinned()) {
      return;
    }
    const measured = measureAvailableWidth();
    if (measured === null || measured === naturalWidthRef.current) {
      return;
    }
    naturalWidthRef.current = measured;
    if (placeholder) {
      placeholder.style.width = `${measured}px`;
    }
    syncHorizontalBox();
  }, [isPinned, measureAvailableWidth, syncHorizontalBox]);

  useEffect(() => {
    const toolbar = toolbarRef.current;
    const placeholder = placeholderRef.current;
    if (!toolbar) {
      return;
    }

    const updatePlaceholder = () => {
      // In fullscreen the toolbar spans the viewport and the placeholder is
      // orphaned in the hidden original tree -- measuring either would cache a
      // width that wrecks the layout on the way back out.
      if (!placeholder || isEditorFullscreen()) {
        return;
      }
      // Reserve the toolbar's normal-flow space while it's fixed. The toolbar
      // sets the column's intrinsic width, so the width matters as much as the
      // height -- without it the column shrinks the moment the toolbar is
      // pinned, and the toolbar shrinks with it.
      placeholder.style.height = `${toolbar.offsetHeight}px`;
      if (!isPinned()) {
        naturalWidthRef.current = toolbar.offsetWidth;
      }
    };
    const toolbarObserver = new ResizeObserver(updatePlaceholder);
    toolbarObserver.observe(toolbar);
    updatePlaceholder();

    const geometryObserver = new ResizeObserver(syncHorizontalBox);
    if (placeholder) {
      geometryObserver.observe(placeholder);
    }

    // Watch the layout viewport, not the window's resize event: that can arrive
    // before the document has been re-laid out. Height changes are ignored --
    // pinning the toolbar is itself one, and re-measuring then is circular.
    let pendingFrame: number | null = null;
    let lastViewportWidth = document.documentElement.clientWidth;
    const viewportObserver = new ResizeObserver(() => {
      const viewportWidth = document.documentElement.clientWidth;
      if (viewportWidth === lastViewportWidth) {
        return;
      }
      lastViewportWidth = viewportWidth;
      if (pendingFrame !== null) {
        cancelAnimationFrame(pendingFrame);
      }
      pendingFrame = requestAnimationFrame(() => {
        pendingFrame = null;
        refreshReservedWidth();
      });
    });
    viewportObserver.observe(document.documentElement);

    return () => {
      toolbarObserver.disconnect();
      geometryObserver.disconnect();
      viewportObserver.disconnect();
      if (pendingFrame !== null) {
        cancelAnimationFrame(pendingFrame);
      }
    };
  }, [isPinned, refreshReservedWidth, syncHorizontalBox]);

  useStickyPosition({
    elementRef: toolbarRef,
    onReposition: (isFixed) => {
      const toolbar = toolbarRef.current;
      const placeholder = placeholderRef.current;
      if (!toolbar) {
        return;
      }
      if (isFixed) {
        if (placeholder) {
          placeholder.style.display = 'block';
          if (naturalWidthRef.current !== null) {
            placeholder.style.width = `${naturalWidthRef.current}px`;
          }
        }
        syncHorizontalBox();
      } else {
        if (placeholder) {
          placeholder.style.display = 'none';
          placeholder.style.width = '';
        }
        toolbar.style.left = '';
        toolbar.style.width = '';
      }
    },
  });

  return (
    <>
      <div ref={placeholderRef} style={{ display: 'none' }} />
      <div ref={toolbarRef} className={className} id={id}>
        {children}
      </div>
    </>
  );
};
